import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../utils/tableVariables';
import { WorkflowProgressValue } from '../../utils/workflow';

type TCTPState = {
  // Pagination
  page: number;
  pageSize: number;

  // Search/Filter
  search: string;
  workflowFilter: WorkflowProgressValue | 'all';

  // Delete Dialog
  isDeleteDialogOpen: boolean;
  ctpToDelete: { id: string; ctpNumber: string } | null;

  // Snackbar notifications
  snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  };
};

const initialState: TCTPState = {
  page: 0,
  pageSize: DEFAULT_TABLE_PAGE_SIZE,
  search: '',
  workflowFilter: 'all',
  isDeleteDialogOpen: false,
  ctpToDelete: null,
  snackbar: {
    open: false,
    message: '',
    severity: 'success',
  },
};

const ctpSlice = createSlice({
  name: 'ctp',
  initialState,
  reducers: {
    // Pagination
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },

    // Search - reset page when search changes
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.page = 0;
    },

    // Workflow filter - reset page when filter changes
    setWorkflowFilter: (state, action: PayloadAction<WorkflowProgressValue | 'all'>) => {
      state.workflowFilter = action.payload;
      state.page = 0;
    },

    // Delete dialog
    openDeleteDialog: (state, action: PayloadAction<{ id: string; ctpNumber: string }>) => {
      state.isDeleteDialogOpen = true;
      state.ctpToDelete = action.payload;
    },
    closeDeleteDialog: (state) => {
      state.isDeleteDialogOpen = false;
      state.ctpToDelete = null;
    },

    // Snackbar
    showSnackbar: (state, action: PayloadAction<{ message: string; severity: 'success' | 'error' | 'warning' | 'info' }>) => {
      state.snackbar = {
        open: true,
        message: action.payload.message,
        severity: action.payload.severity,
      };
    },
    hideSnackbar: (state) => {
      state.snackbar.open = false;
    },

    // Reset all state
    resetCTPState: () => initialState,
  },
});

export const {
  setPage,
  setPageSize,
  setSearch,
  setWorkflowFilter,
  openDeleteDialog,
  closeDeleteDialog,
  showSnackbar,
  hideSnackbar,
  resetCTPState,
} = ctpSlice.actions;

export default ctpSlice.reducer;
